import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { ReactComponent as Close } from "../../assets/svg/close.svg";
import { ReactComponent as CopyIcon } from "../../assets/svg/copy.svg";
import errorIcon from "../../assets/svg/error.svg";
import {
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS, FONT_SIZE } from "../../constants/design/fontSize";
import { BORDER_RADIUS, SPACING } from "../../constants/design/spacing";
import useKeydownHandler, {
  KeydownHandlerKeyEnum,
} from "../../hooks/useKeydownHandler";
import useOutsideAlerter from "../../hooks/outsideAlerter/useOutsideAlerter";
import { formatPotentialContractError } from "../../utils/errors";

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const DialogWrapper = styled(motion.div)`
  background: ${LAYER_COLORS.two};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: ${BORDER_RADIUS.five}px;
  width: 400px;
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.three}px;
  border-bottom: 1px solid ${BORDER_COLORS.one};
  gap: ${SPACING.two}px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
`;

const ErrorIconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.span`
  ${FONT_CSS.header.seven}
  color: ${COLORS.negative.one};
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${BORDER_RADIUS.one}px;
  transition: background 0.15s ease;

  &:hover {
    background: ${COLORS.white.two};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Content = styled.div`
  padding: ${SPACING.three}px;
  overflow-y: auto;
  flex: 1;
`;

const ErrorMessage = styled.div`
  ${FONT_CSS.body.five}
  color: ${TEXT_COLORS.two};
  word-break: break-word;
  white-space: pre-wrap;
  font-family: monospace;
  background: ${LAYER_COLORS.one};
  padding: ${SPACING.two}px;
  border-radius: ${BORDER_RADIUS.one}px;
  max-height: 300px;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  gap: ${SPACING.two}px;
  padding: ${SPACING.three}px;
  border-top: 1px solid ${BORDER_COLORS.one};
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.two}px;
  border-radius: ${BORDER_RADIUS.two}px;
  font-size: ${FONT_SIZE.two};
  transition: all 0.15s ease;

  ${({ $variant }) =>
    $variant === "primary"
      ? `
    background: ${COLORS.negative.one};
    color: ${TEXT_COLORS.one};

    &:hover {
      background: ${COLORS.negative.two};
    }
  `
      : `
    background: ${LAYER_COLORS.three};
    color: ${TEXT_COLORS.one};

    &:hover {
      background: ${LAYER_COLORS.four};
    }
  `}

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CopiedText = styled.span`
  color: ${COLORS.positive.one};
  font-size: ${FONT_SIZE.one};
`;

export interface IErrorDialogData {
  header: string;
  message: string;
  /**
   * Optional call to action, shown beside Close.
   *
   * Some errors are things the user can fix in one click — a wallet on the wrong
   * network being the case this was added for. Telling them what is wrong and
   * leaving them to find the fix themselves is a worse dialog than one that
   * offers it. Omitted by every existing caller, so nothing else changes.
   */
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface IErrorDialogProps {
  error: IErrorDialogData | null;
  onClose: () => void;
}

export function ErrorDialog({ error, onClose }: IErrorDialogProps) {
  const [copied, setCopied] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const displayError = useMemo(() => {
    if (!error) return null;
    return {
      ...error,
      message: formatPotentialContractError(error.message) || error.message,
    };
  }, [error]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useOutsideAlerter([dialogRef], handleClose);
  useKeydownHandler(KeydownHandlerKeyEnum.ESC, handleClose);

  const handleCopy = useCallback(async () => {
    if (!displayError) return;

    const textToCopy = `Error: ${displayError.header}\n\nDetails:\n${displayError.message}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [displayError]);

  const root = document.getElementById("modal-portal");

  if (!root) return null;

  return createPortal(
    <AnimatePresence>
      {displayError && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <DialogWrapper
            ref={dialogRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
          >
            <Header>
              <HeaderLeft>
                <ErrorIconWrapper>
                  <img src={errorIcon} alt="Error" />
                </ErrorIconWrapper>
                <Title>{displayError.header}</Title>
              </HeaderLeft>
              {/*
                An icon-only button with no text and no label has NO accessible
                name — a screen reader announces it as just "button". Name it,
                and mark the icon decorative so it adds nothing of its own.
                "Close dialog" rather than "Close" because the footer already has
                a button called Close; two controls with the same name in one
                dialog is ambiguous read aloud.
              */}
              <CloseButton onClick={handleClose} aria-label="Close dialog">
                <Close aria-hidden="true" focusable="false" />
              </CloseButton>
            </Header>
            <Content>
              <ErrorMessage>{displayError.message}</ErrorMessage>
            </Content>
            <Footer>
              <Button $variant="secondary" onClick={handleCopy}>
                <CopyIcon aria-hidden="true" focusable="false" />
                {copied ? <CopiedText>Copied!</CopiedText> : "Copy Details"}
              </Button>
              {displayError.action ? (
                <Button
                  $variant="primary"
                  onClick={() => {
                    // Close first: the action opens the wallet's own modal (a
                    // network switch prompt), and leaving this dialog stacked
                    // over it hides the thing the user has to answer.
                    handleClose();
                    displayError.action?.onClick();
                  }}
                >
                  {displayError.action.label}
                </Button>
              ) : null}
              <Button
                $variant={displayError.action ? "secondary" : "primary"}
                onClick={handleClose}
              >
                Close
              </Button>
            </Footer>
          </DialogWrapper>
        </Overlay>
      )}
    </AnimatePresence>,
    root
  );
}
